import { validateItemName, validateTwoNumbers } from '@/utilities/Validation';
import { Form, Formik } from 'formik';
import type { FC } from 'react';
import React, { useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { Floor } from '../../models/Floor';
import { IOModule } from '../../models/IOModule';
import { MapRelay } from '../../models/MapRelay';
import { Reader } from '../../models/Reader';
import WizardSideBarStep from '../../models/WizardSideBarStep';
import { saveLift } from '../../services/liftService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { useDialog } from '../dialog/DialogContext';
import Wizard from '../wizard/wizardTwo';
import BuildingStep from './BuildingStep';
import DetailStep from './DetailStep';
import FloorStep from './FloorStep';
import IoDeviceStep from './IoDeviceStep';
import MapRelayStep from './MapRelayStep';
import ReaderStep from './ReaderStep';
import SummaryStep from './SummaryStep';
import AutoCreateStep from './AutoCreateStep';

interface LiftModalProps {
    isOpen: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onSaveSuccess: () => void;
    setBusy: React.Dispatch<React.SetStateAction<boolean>>;
    setBusyMessage: React.Dispatch<React.SetStateAction<string>>;
}

const LiftModal: FC<LiftModalProps> = ({ isOpen, setOpen, onSaveSuccess, setBusy, setBusyMessage }) => {
    const initialValues = {
        id: 0,
        name: '',
        siteId: '',
        description: '',
        buildingId: '',
        buildingName: '',
        startingFloor: undefined,
        lastFloor: undefined,
        floorIds: [] as string[],
        ioDeviceIds: [] as string[],
        readerId: '',
        readerName: '',
        readerFixedAddress: '',
        floors: [] as Floor[],
        ioModules: [] as IOModule[],
        readers: [] as Reader[],
        mapRelays: [] as MapRelay[],
        autoCreateFloorGroup: true,
        autoCreateFullAccessFloorGroup: true,
        autoCreateAccessProfile: true
    }
    const dialog = useDialog();
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(0);
    const [currentItem, setCurrentItem] = useState(initialValues);
    const [stepDescriptions, setStepDescriptions] = useState<{[key: string]: string}>({
        step1: '',
        step2: '',
        step3: '',
        step4: '',
        step5: '',
        step6: '',
        step7: ''
    });
    const steps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.details),
            description: stepDescriptions.step1,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.building),
            description: stepDescriptions.step2,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.floors),
            description: stepDescriptions.step3,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.readers),
            description: stepDescriptions.step4,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.ioDevices),
            description: stepDescriptions.step5,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.mapRelays),
            description: stepDescriptions.step6,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.permissions),
            description: stepDescriptions.step7,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const onNext = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setStep(prev => prev + 1);
    }

    const onBack = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setStep(prev => prev - 1);
    }

    const content: JSX.Element[] = [
        <DetailStep key={"lift-detail-step"} onNext={onNext} setStepDescriptions={setStepDescriptions}/>,
        <BuildingStep key={"lift-building-step"} onNext={onNext} onBack={onBack} setStepDescriptions={setStepDescriptions} />,
        <FloorStep key={"lift-floors-step"} onNext={onNext} onBack={onBack} setStepDescriptions={setStepDescriptions} />,
        <ReaderStep key={"lift-readers-step"} onNext={onNext} onBack={onBack} setStepDescriptions={setStepDescriptions} />,
        <IoDeviceStep key={"lift-io-devices-step"} onNext={onNext} onBack={onBack} setStepDescriptions={setStepDescriptions} />,
        <MapRelayStep key={"lift-map-relays-step"} onNext={onNext} onBack={onBack} setStepDescriptions={setStepDescriptions} />,
        <AutoCreateStep key={"lift-auto-create-step"} onNext={onNext} onBack={onBack} />,
        <SummaryStep key={"lift-summary-step"} onBack={onBack}/>,
    ];

    const onSave = (values: any) => {
        setBusy(true);
        setBusyMessage(translate.byKey(TranslationKeys.savingLiftPleaseWait));
        const liftToSave = {
            floorLEvelStart: values.startingFloor,
            floorLevelEnd: values.lastFloor,
            name: values.name,
            desc: values.description,
            siteId: values.siteId,
            buildingId: values.buildingId,
            floorIds: values.floorIds,
            readerId: values.readerId,
            mapRelays: values.mapRelays,
            autoCreateFloorGroupsPerFloor: values.autoCreateFloorGroup,
            autoCreateFullAccessFloorGroup: values.autoCreateFullAccessFloorGroup,
            autoCreateAccessProfile: values.autoCreateAccessProfile
        }

        saveLift(liftToSave).then(() => {
            setOpen(false);
            onSaveSuccess();
            setBusy(false); // Only close me if we created successfully
            toast.success(translate.byKey(TranslationKeys.liftSavedSuccessfully));
        }).catch((error) => {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error)
            })
        }).finally(() => {
            setBusyMessage('');
        });
    }

    const onClose = () => {
        setOpen(false);
        setStep(0);
        setStepDescriptions({
            step1: '',
            step2: '',
            step3: '',
            step4: '',
            step5: '',
            step6: ''
        });
        setCurrentItem(initialValues);
    }

    const validateForm = (values: any) => {
        const errors: any = {};

        errors.name = validateItemName(values.name, translate);
        errors.startingFloor = validateTwoNumbers(values.startingFloor, values.lastFloor, translate.byKey(TranslationKeys.startingFloor), translate.byKey(TranslationKeys.lastFloor), ['NOTGREATERTHAN', 'NOTEQUAL'], translate);
       
        // Don't validate last floor if starting floor is invalid to avoid multiple error messages for the same issue
        if (!errors.startingFloor) {
            errors.lastFloor = validateTwoNumbers(values.lastFloor, values.startingFloor, translate.byKey(TranslationKeys.lastFloor), translate.byKey(TranslationKeys.startingFloor), ['NOTLESSTHAN', 'NOTEQUAL'], translate);
        }

        //remove all undefined errors
        Object.keys(errors).forEach(key => {
            if (errors[key] === undefined) {
                delete errors[key];
            }
        });
        
        return errors;
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none w-content">
        <Formik
            enableReinitialize={true}
            initialValues={currentItem}
            onSubmit={onSave}
            validate={validateForm}>
            <Form>
                <Wizard
                    name={translate.byKey(TranslationKeys.addLift)}
                    step={step}
                    onClose={() => onClose()}
                    sidebarSteps={steps}
                    stepContent={content} />
            </Form>
        </Formik>
    </Modal>
}

export default LiftModal;